import React, { useRef, useState, useContext, useEffect } from "react";

import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import FilterListOutlinedIcon from "@mui/icons-material/FilterListOutlined";
import { SimpleTooltip } from "simple-ui-components";

import {
  BodyWrapperStyled,
  BoxStyled,
  CardStyled,
  CardHeader,
  CardBody,
  OuterWrapper,
  EmptyList,
} from "../../theme";
import { AddEditAccount } from "./addEditAccount";
import { AuthContext } from "../authContextProvider";
import { del, get } from "../../server-requests/serverRequests";
import DeletePopup from "../shared/deletePopup";
import { Layout, useWindowSize } from "../layout/otherPage";
import { LoadingList } from "../shared/loading";
import { AccountDetail } from "./accountDetail";
import { FilterAccount } from "./filterAccount";
import { getTranslation, LanguageContext } from "../languageProvider";
import { Log, ReplaceWord } from "../shared/utility";

export const Accounts = () => {
  const addEditAccountRef = useRef();
  const deleteAccountRef = useRef();

  const { Tooltip } = SimpleTooltip;

  const { wrapperHeight, boxHeight } = useWindowSize();

  const { snackbarHandler } = useContext(AuthContext);
  const { lang } = useContext(LanguageContext);

  const [reloadAccounts, setReloadAccounts] = useState(true);
  const [loadingAccount, setLoadingAccount] = useState(true);
  const [accountList, setAccountList] = useState([]);
  const [accountListFull, setAccountListFull] = useState([]);

  const [clientList, setClientList] = useState([]);

  const [clickedAccount, setClickedAccount] = useState("");

  const [filter, setFilter] = useState([]);
  const [filterAccount, setFilterAccount] = useState(false);

  // Accounts
  const accountAddClickHandler = () =>
    addEditAccountRef.current.AddEditAccountHandler("add", clientList);

  const accountEditClickHandler = (account) =>
    addEditAccountRef.current.AddEditAccountHandler("edit", account);

  const updateAccountSuccessHandler = () => setReloadAccounts(true);

  const accountDeleteClickHandler = (account) => {
    deleteAccountRef.current.Delete(
      account.id,
      `${getTranslation("0032")} ${
        getTranslation("0073") && getTranslation("0073").toLowerCase()
      }`,
      getTranslation("0120") &&
        ReplaceWord(
          getTranslation("0120"),
          "**NAME**",
          getTranslation("0073").toLowerCase()
        )
    );
    setClickedAccount(account);
  };

  const deleteAccountHandler = (id) => {
    let params = {
      id,
      email: clickedAccount.email,
    };
    del("accounts", params)
      .then(() => {
        snackbarHandler(
          getTranslation("0121") &&
            ReplaceWord(
              getTranslation("0121"),
              "**NAME**",
              getTranslation("0073")
            ),
          "success"
        );
        setReloadAccounts(true);
      })
      .catch((status) => {
        snackbarHandler(getTranslation(`error${status}`));
        Log("Error: delete accounts" + status);
      });
  };

  // get clients list
  useEffect(() => {
    let isMounted = true;
    get("clients/list")
      .then((client) => {
        if (isMounted) setClientList(client);
      })
      .catch((status) => {
        snackbarHandler(getTranslation(`error${status}`));
        Log("Error: get clients/list" + status);
      });
    return () => (isMounted = false);
  }, [snackbarHandler]);

  // get Account list
  useEffect(() => {
    let isMounted = true;
    if (reloadAccounts) {
      get("accounts/list")
        .then((account) => {
          if (isMounted) {
            setAccountList(account);
            setAccountListFull(account);
            setReloadAccounts(false);
            setLoadingAccount(false);
          }
        })
        .catch((status) => {
          setLoadingAccount(false);
          setReloadAccounts(false);
          snackbarHandler(getTranslation(`error${status}`));
          Log("Error: get accounts/list" + status);
        });
    }
    return () => (isMounted = false);
  }, [reloadAccounts, snackbarHandler]);

  // Filter List
  useEffect(() => {
    if (filterAccount) {
      let list = accountListFull;
      if (filter) {
        if (filter.nameEmail !== "") {
          list = list.filter((account) => {
            let accountName = account.name ? account.name.toUpperCase() : "";
            let accountEmail = account.email ? account.email.toUpperCase() : "";
            let items = accountName.includes(filter.nameEmail.toUpperCase());
            if (!items) {
              items = accountEmail.includes(filter.nameEmail.toUpperCase());
            }
            return items;
          });
        }
        if (filter.accountType !== "0") {
          list = list.filter(
            (account) => account.role.code === Number(filter.accountType)
          );
        }
      }
      setAccountList(list);
      setFilterAccount(false);
    }
  }, [accountListFull, filterAccount, filter]);

  const filterAccountChangeHandler = (filterBy) => {
    setFilter(filterBy);
    setFilterAccount(true);
  };

  return (
    <Layout lang={lang}>
      <OuterWrapper height={wrapperHeight}>
        <BodyWrapperStyled fullHeight>
          <BoxStyled width="xs" col="2" noMargin>
            <CardStyled>
              <CardHeader>
                <FilterListOutlinedIcon className="mainIcon" />
                <div className="detail" data-translate="0076">
                  {getTranslation("0076")}
                  <span data-translate="0073,0077">
                    {ReplaceWord(
                      getTranslation("0077"),
                      "**LIST**",
                      getTranslation("0073").toLowerCase() + "s"
                    )}
                  </span>
                </div>
              </CardHeader>
              <CardBody height={boxHeight}>
                <FilterAccount Filter={filterAccountChangeHandler} />
              </CardBody>
            </CardStyled>
          </BoxStyled>
          <BoxStyled width="xl" col="2" noMargin>
            <CardStyled>
              <CardHeader>
                <PeopleAltIcon className="mainIcon" />
                <div className="detail" data-translate="0073">
                  {getTranslation("0073")}s
                  <span data-translate="0073,0078">
                    {ReplaceWord(
                      getTranslation("0078"),
                      "**NUMBER**",
                      accountList.length,
                      "**LISTNAME**",
                      getTranslation("0073").toLowerCase() + "s"
                    )}
                  </span>
                </div>
                <Tooltip
                  text={`${getTranslation("0087")} ${getTranslation(
                    "0073"
                  ).toLowerCase()}`}
                  placement="left"
                >
                  <AddOutlinedIcon onClick={accountAddClickHandler} />
                </Tooltip>
              </CardHeader>
              <CardBody height={boxHeight}>
                {loadingAccount && <LoadingList count={5} />}
                {!loadingAccount && accountList.length === 0 && (
                  <EmptyList data-translate="0049,0123">
                    {ReplaceWord(
                      getTranslation("0123"),
                      "**NAME**",
                      getTranslation("0049").toLowerCase()
                    )}
                  </EmptyList>
                )}
                {!loadingAccount &&
                  accountList.length > 0 &&
                  accountList.map((account, index) => (
                    <AccountDetail
                      key={index}
                      account={account}
                      Edit={accountEditClickHandler}
                      Delete={accountDeleteClickHandler}
                    />
                  ))}
              </CardBody>
            </CardStyled>
          </BoxStyled>
        </BodyWrapperStyled>
        <AddEditAccount
          ref={addEditAccountRef}
          UpdateSuccess={updateAccountSuccessHandler}
        />
        <DeletePopup
          ref={deleteAccountRef}
          DeleteSuccess={deleteAccountHandler}
        />
      </OuterWrapper>
    </Layout>
  );
};
